import axiosServices from '../utils/axios';
const backendUrl = import.meta.env.VITE_BACKEND_URL;

export const fetchSharedStudies = async () => {
  const response = await axiosServices.get(`${backendUrl}/workspaces/shared/studies`);
  return response.data;
};
export const fetchStudies = async (paginationModel) => {
  const response = await axiosServices.get(`${backendUrl}/dynamic-surveys/`, {
    params: paginationModel,
  });
  return response.data;
};
export const fetchRecentStudies = async () => {
  const response = await axiosServices.get(`${backendUrl}/dynamic-surveys/recent_studies`);
  return response.data;
};
export const fetchStudy = async (id) => {
  const response = await axiosServices.get(`${backendUrl}/dynamic-surveys/${id}/`);
  return response.data;
};
export const fetchSurveyDetails = async (id) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${id}/finalizable-details/`,
  );
  return response.data;
};
export const generateStudy = async (data) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/generate/`, data);
  return response.data;
};
export const postStudy = async (data) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/`, data);
  return response.data;
};
export const updateStudy = async ({ id, transformedData }) => {
  const response = await axiosServices.put(`${backendUrl}/dynamic-surveys/${id}/`, transformedData);
  return response.data;
};
export const stopCollectingResponsesSurvey = async ({ survey_id, disable_reason }) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/${survey_id}/disable-study/?disable_reason=${disable_reason}`,
  );
  return response.data;
};
export const deleteStudies = async (id) => {
  const response = await axiosServices.delete(`${backendUrl}/dynamic-surveys/${id}/`);
  return response.data;
};
export const duplicateSurvey = async (input) => {
  // Backwards-compatible: callers can pass either a bare id (legacy) or
  // an object { id, sourceLanguage, targetLanguage } when they want the
  // duplicate to be translated. sourceLanguage is optional — the backend
  // will ask the LLM to auto-detect when it's missing.
  const id = typeof input === 'object' ? input?.id : input;
  const sourceLanguage = typeof input === 'object' ? input?.sourceLanguage : null;
  const targetLanguage = typeof input === 'object' ? input?.targetLanguage : null;
  const title = typeof input === 'object' ? input?.title : null;

  const params = { study_id: id };
  if (targetLanguage) {
    params.target_language = targetLanguage;
    if (sourceLanguage) {
      params.source_language = sourceLanguage;
    }
  }
  if (title?.trim()) {
    params.title = title.trim();
  }

  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/`, null, { params });
  return response.data;
};
export const createLinkBasedShareSurvey = async ({ surveyId, returnUrl }) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/link-based-share-request/${surveyId}/?return_url=${returnUrl}`,
  );
  return response.data;
};
export const revokeLinkBasedShareSurvey = async ({ surveyId, token }) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/link-based-share-request/${surveyId}/revoke/?token=${token}`,
  );
  return response.data;
};
export const uploadStudies = async ({ formData, onProgress, signal }) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/upload/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal,
    onUploadProgress: (progressEvent) => {
      const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onProgress?.(percentCompleted);
    },
  });
  return response.data;
};
export const uploadAnalyzedStudy = async ({ id, payload }) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic_survey/${id}/question_sections`,
    payload,
  );
  return response.data;
};
export const updateStudyFinalize = async ({ id, transformedData }) => {
  const response = await axiosServices.patch(
    `${backendUrl}/dynamic-surveys/${id}/`,
    transformedData,
  );
  return response.data;
};
export const fetchSurveyDetailsAnalysis = async ({ id }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${id}/survey-chart-data/`,
  );
  return response.data;
};
export const fetchInterviewDetailsAnalysis = async ({ id }) => {
  const response = await axiosServices.get(`${backendUrl}/qualitative-studies/${id}/chart_data/`);
  return response.data;
};
export const createCode = async (payload) => {
  const response = await axiosServices.patch(`${backendUrl}/dynamic-surveys/codes/create`, payload);
  return response.data;
};
export const searchAndApplyCode = async (payload) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/apply-open-code`,
    payload,
  );
  return response.data;
};

export const assignCode = async (payload) => {
  const response = await axiosServices.patch(`${backendUrl}/dynamic-surveys/codes/assign`, payload);
  return response.data;
};

export const unassignCode = async (payload) => {
  const response = await axiosServices.patch(
    `${backendUrl}/dynamic-surveys/codes/unassign`,
    payload,
  );
  return response.data;
};

export const deleteCode = async (payload) => {
  const response = await axiosServices.delete(`${backendUrl}/dynamic-surveys/codes/delete`, {
    data: payload,
  });
  return response.data;
};

export const updateCode = async (payload) => {
  const response = await axiosServices.patch(`${backendUrl}/dynamic-surveys/codes/edit`, payload);
  return response.data;
};

export const fetchSurveyQuestionAnalysis = async ({ id }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${id}/question_analysis/`,
  );
  return response.data;
};
export const analyzeDynamicSurvey = async (id, workspaceId) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/${id}/analyze/?workspace_id=${workspaceId}`,
  );
  return response.data;
};

export const analyzeQuestionElaboration = async (
  survey_id,
  section_id,
  question_id,
  question_text,
) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/question_reanalyze`, {
    section_id,
    question_id,
    question_text,
    survey_id,
  });
  return response.data;
};

export const uploadAndSaveSurvey = async (payload, workspaceId) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/save_and_analyze?workspace_id=${workspaceId}`,
    payload,
  );
  return response.data;
};
export const fetchSurveyParticipant = async (paginationModel, surveyId) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/survey/${surveyId}/participant/`,
    {
      params: paginationModel,
    },
  );
  return response.data;
};
export const fetchInterviewParticipant = async (paginationModel, surveyId) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${surveyId}/participant/`,
    {
      params: paginationModel,
    },
  );
  return response.data;
};
export const fetchSingleInterviewParticipant = async (participantId, surveyId) => {
  const response = await axiosServices.get(
    `${backendUrl}/qualitative-studies/${surveyId}/participant/`,
    { params: { transcript_id: participantId } },
  );
  return response.data;
};
export const fetchSurveyQuestionAnalysisById = async ({
  surveyId,
  sectionId,
  questionId,
  selected_questions,
}) => {
  let url = `${backendUrl}/dynamic-surveys/survey/${surveyId}/section/${sectionId}/question/${questionId}/`;
  if (selected_questions) {
    url = url + `?selected_questions=${JSON.stringify(selected_questions)}`;
  }
  const response = await axiosServices.get(url);

  return response.data;
};
export const fetchSurveyQverallAnalysis = async ({ surveyId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/survey_overall_analysis/${surveyId}/`,
  );
  return response.data;
};
export const fetchParticipantCountries = async (id) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/participant-countries/${id}/`,
  );
  return response.data;
};
export const fetchParticipantDevice = async (id) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/participant-devices/${id}/`,
  );
  return response.data;
};
export const fetchParticipantResponseById = async ({ participantId, surveyId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/survey/${surveyId}/participant-response/${participantId}/`,
  );
  return response.data;
};
export const fetchSourceAndContext = async ({ surveyId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/source-context/`,
  );
  return response.data;
};

export const searchSurveyResponses = async ({ surveyId, sectionId, questionId, search_term }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/get_keyword_search/${surveyId}/section/${sectionId}/question/${questionId}/`,
    { params: { search_term } },
  );
  return response.data;
};

export const fetchSurveyFilterContext = async ({ surveyId }) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/response_filter_context/`,
  );
  return response.data;
};
export const predictStudyTitle = async (data) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/ai/predict_title/`,
    data,
  );
  return response.data;
};
export const sendAndGetMessage = async (data) => {
  const response = await axiosServices.post(`${backendUrl}/chat_with_transcripts`, data);
  return response.data;
};

export const fetchQuestionResponses = async (
  surveyId,
  questionId,
  { offset = 0, limit = 10, search_term = '', search_type = 'keyword', threshold, sentiment } = {},
) => {
  const params = { offset, limit };
  if (search_term) {
    params[search_type] = search_term;
  }
  if (search_type === 'semantic' && threshold !== undefined) {
    params.threshold = threshold;
  }
  if (sentiment) {
    params.sentiment = sentiment;
  }

  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/questions/${questionId}/responses`,
    { params },
  );
  return response.data;
};

/**
 * List the language codes a survey has translated responses available
 * for. Returns ``["es", "fr"]`` etc. (empty if none yet). Used to
 * populate the language selector on the question-analysis page.
 */
export const fetchSurveyTranslationLanguages = async (surveyId) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/translations/languages`,
  );
  return response.data;
};

/**
 * Get every translated response for one question in one language.
 * Backend returns a flat list so we can index by either response_id
 * (paginated responses endpoint) OR participant_id (analysis endpoint's
 * `raw_response` which doesn't carry response_id).
 */
export const fetchQuestionTranslations = async (surveyId, questionId, language) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/questions/${questionId}/translations`,
    { params: { language } },
  );
  return response.data;
};

export const fetchQuestionTheme = async (surveyId, questionId) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/questions/${questionId}/themes`,
  );
  return response.data;
};
export const fetchQuestionCodes = async (surveyId, questionId) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/questions/${questionId}/codes`,
  );
  return response.data;
};
export const fetchQuestionSentimentStat = async (surveyId, questionId) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/question/${questionId}/sentiment-stats`,
  );
  return response.data;
};
export const fetchQuestionCodeDetails = async (surveyId, questionId, code) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/${surveyId}/questions/${questionId}/codes/${code}`,
  );
  return response.data;
};

export const updateTheme = async (data) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/themes/update`, data);
  return response.data;
};

export const createTheme = async (data) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/themes/add`, data);
  return response.data;
};

export const deleteTheme = async (data) => {
  const response = await axiosServices.post(`${backendUrl}/dynamic-surveys/themes/delete`, data);
  return response.data;
};

export const updateOverallAnalysis = async (surveyId) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/${surveyId}/regenerate-overall-analysis`,
  );
  return response.data;
};

export const generateThemeDefination = async (data) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/themes/generate-definition-summary`,
    data,
  );
  return response.data;
};

export const exportParticipantData = async (studyId) => {
  const response = await axiosServices.get(
    `${backendUrl}/dynamic-surveys/survey/${studyId}/participant/export`,
  );
  return response.data;
};

export const fetchStudyReports = async ({ id, paginationModel }) => {
  const response = await axiosServices.get(`${backendUrl}/report/study/${id}/`, {
    params: paginationModel,
  });
  return response.data;
};

// Stimulus upload API calls
export const generateStimulusUploadUrl = async (surveyId, data) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/${surveyId}/stimulus/generate-upload-url`,
    data,
  );
  return response.data;
};

export const commitStimulusBlocks = async (surveyId, data) => {
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/${surveyId}/stimulus/commit-blocks`,
    data,
  );
  return response.data;
};

export const deleteStimulusImage = async (surveyId, blobName) => {
  const response = await axiosServices.delete(
    `${backendUrl}/dynamic-surveys/${surveyId}/stimulus/delete-image?blob_name=${encodeURIComponent(blobName)}`,
  );
  return response.data;
};

/**
 * Kick off an async export of all survey responses to CSV. The CSV is
 * built in the long-running-tasks worker and delivered via a
 * notification carrying a download URL — this just enqueues the job and
 * returns immediately (202), so the browser never hangs building it.
 *
 * Pass `{ translate: true, targetLanguage, sourceLanguage }` to have the
 * worker translate the question columns + responses. Source is optional
 * (auto-detected when omitted). Translation is capped server-side at 500
 * responses; over that the request 400s with an enterprise message.
 */
export const exportSurveyResponses = async (surveyId, options = {}) => {
  const { translate = false, sourceLanguage = null, targetLanguage = null } = options;
  const params = {};
  if (translate) {
    params.translate = true;
    if (targetLanguage) params.target_language = targetLanguage;
    if (sourceLanguage) params.source_language = sourceLanguage;
  }
  const response = await axiosServices.post(
    `${backendUrl}/dynamic-surveys/${surveyId}/responses/export`,
    null,
    { params },
  );
  return response.data;
};
